const ACTIVITY_APP = 'activity-planner';
const MEAL_APP = 'meal-manager';

export const THIS_APP = MEAL_APP;

const LOCAL_PORTS = {
  [ACTIVITY_APP]: 4028,
  [MEAL_APP]: 5173,
};

const ACTIVITY_TYPES = new Set([
  'resource_pending',
  'resource_approved',
  'resource_rejected',
  'activity_share_pending',
  'activity_share_approved',
  'activity_share_rejected',
]);

const MEAL_TYPES = new Set([
  'meal_pending',
  'meal_approved',
  'meal_rejected',
]);

const COMMUNITY_TYPES = new Set([
  'like',
  'dislike',
  'upvote',
  'downvote',
  'comment',
  'reply',
  'broadcast_post',
]);

export const detectHostFamily = (hostname) => {
  const host = String(hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return 'local';
  if (host === 'maap.care' || host.endsWith('.maap.care')) return 'maap';
  if (host === 'happyday.care' || host.endsWith('.happyday.care')) return 'happyday';
  return 'other';
};

const siblingOrigins = (family, loc) => {
  if (family === 'maap') {
    return {
      [ACTIVITY_APP]: 'https://www.activities.maap.care',
      [MEAL_APP]: 'https://www.menu.maap.care',
    };
  }
  if (family === 'happyday') {
    return {
      [ACTIVITY_APP]: 'https://activities.happyday.care',
      [MEAL_APP]: 'https://meal-manager.happyday.care',
    };
  }
  if (family === 'local') {
    const protocol = loc?.protocol || 'http:';
    const host = loc?.hostname || 'localhost';
    return {
      [ACTIVITY_APP]: `${protocol}//${host}:${LOCAL_PORTS[ACTIVITY_APP]}`,
      [MEAL_APP]: `${protocol}//${host}:${LOCAL_PORTS[MEAL_APP]}`,
    };
  }
  return null;
};

export const originForApp = (appKey, loc = typeof window !== 'undefined' ? window.location : null) => {
  if (!loc) return null;
  const map = siblingOrigins(detectHostFamily(loc.hostname), loc);
  if (!map) return null;
  return map[appKey] || null;
};

const parseMaybeUrl = (raw, baseOrigin) => {
  const value = String(raw || '').trim();
  if (!value) return null;
  try {
    return new URL(value, baseOrigin || 'https://notification.local');
  } catch {
    return null;
  }
};

const pathFromUrl = (url) => {
  if (!url) return '';
  return `${url.pathname || '/'}${url.search || ''}${url.hash || ''}`;
};

const isGenericPath = (path, type) => {
  const parsed = parseMaybeUrl(path, 'https://notification.local');
  if (!parsed) return true;
  const pathname = (parsed.pathname || '/').replace(/\/+$/, '') || '/';
  const hasPost = Boolean(parsed.searchParams.get('post'));
  if (pathname === '/') return !parsed.search && !parsed.hash;
  if (pathname === '/community' && !hasPost && !COMMUNITY_TYPES.has(type)) return true;
  return false;
};

const storedQueryValue = (path, key) => {
  const parsed = parseMaybeUrl(path, 'https://notification.local');
  return parsed?.searchParams.get(key) || '';
};

const fallbackPath = ({ type, postId, sourceApp, storedPath, thisApp }) => {
  if (type === 'support_ticket_new' || type === 'support_ticket_followup') {
    return sourceApp
      ? `/super-admin/support-tickets?app=${encodeURIComponent(sourceApp)}`
      : '/super-admin/support-tickets';
  }
  if (type === 'onboarding_completed') {
    return thisApp === MEAL_APP ? '/super-admin?tab=onboarding' : '/super-admin/onboarding';
  }
  if (type === 'resource_pending') {
    const resourceId = storedQueryValue(storedPath, 'resource');
    return resourceId
      ? `/resources?tab=review&resource=${encodeURIComponent(resourceId)}`
      : '/resources?tab=review';
  }
  if (type === 'resource_approved' || type === 'resource_rejected') {
    const resourceId = storedQueryValue(storedPath, 'resource');
    return resourceId
      ? `/resources?tab=my&resource=${encodeURIComponent(resourceId)}`
      : '/resources?tab=my';
  }
  if (type === 'activity_share_pending') return '/activities?tab=shares';
  if (type === 'activity_share_approved' || type === 'activity_share_rejected') {
    const activityId = storedQueryValue(storedPath, 'activity');
    return activityId ? `/activities?activity=${encodeURIComponent(activityId)}` : '/activities';
  }
  if (type === 'meal_pending') return '/super-admin/meal-approvals';
  if (type === 'meal_approved' || type === 'meal_rejected') return '/meals-management';
  if (postId) return `/community?post=${encodeURIComponent(postId)}`;
  return '/community';
};

const targetAppForType = (type, thisApp) => {
  if (ACTIVITY_TYPES.has(type)) return ACTIVITY_APP;
  if (MEAL_TYPES.has(type)) return MEAL_APP;
  return thisApp;
};

const withLeadingSlash = (path) => {
  const value = String(path || '/');
  return value.startsWith('/') ? value : `/${value}`;
};

export const resolveNotificationClick = ({
  type,
  sourceUrl,
  sourceApp,
  postId,
  ticketId,
  thisApp = THIS_APP,
  location: loc = typeof window !== 'undefined' ? window.location : null,
} = {}) => {
  if (type === 'support_ticket_resolved' || type === 'support_ticket_reply') {
    if (ticketId) return { action: 'ticket-modal', ticketId };
    return { action: 'navigate', path: '/super-admin/support-tickets' };
  }

  if (type === 'onboarding_completed') {
    return {
      action: 'navigate',
      path: thisApp === MEAL_APP ? '/super-admin?tab=onboarding' : '/super-admin/onboarding',
    };
  }

  const stored = parseMaybeUrl(sourceUrl, loc?.origin);
  const storedPath = stored ? pathFromUrl(stored) : '';
  const path = withLeadingSlash(
    storedPath && !isGenericPath(storedPath, type)
      ? storedPath
      : fallbackPath({ type, postId, sourceApp, storedPath, thisApp })
  );

  const app = targetAppForType(type, thisApp);
  if (app === thisApp) {
    return { action: 'navigate', path };
  }

  const origin = originForApp(app, loc);
  if (!origin || origin === loc?.origin) {
    return { action: 'navigate', path };
  }

  return { action: 'assign', href: `${origin}${path}` };
};

export const applyNotificationClick = (result, { navigate, openResolvedTicket } = {}) => {
  if (!result) return;
  if (result.action === 'ticket-modal') {
    openResolvedTicket?.(result.ticketId);
    return;
  }
  if (result.action === 'navigate' && result.path) {
    navigate?.(result.path);
    return;
  }
  if (result.action === 'assign' && result.href && typeof window !== 'undefined') {
    window.location.assign(result.href);
  }
};
